import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { 
  Send, AlertCircle, FileText, CheckCircle2, Info

} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils';
import toast from 'react-hot-toast';

interface JobApplyProps {
  jobId: number;
  jobTitle: string;
  companyName: string;
}

export function JobApply({ jobId, jobTitle, companyName }: JobApplyProps) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');

  const handleApply = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!user?.worker_id) {
      setError("Vous devez avoir un profil candidat pour postuler");
      toast.error("Vous devez avoir un profil candidat pour postuler");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const applicationData = {
        job_id: jobId,
        worker_id: user.worker_id,
        cover_letter: coverLetter,
        status: 'pending'
      };

      const response = await fetch('https://x1lc-aku3-stjs.p7.xano.io/api:moW4brDB/application', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify(applicationData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erreur lors de l'envoi de la candidature");
      }

      setSuccess(true);
      toast.success('Candidature envoyée avec succès !');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Une erreur est survenue';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Postuler à cette offre
        </h2>
        <p className="text-gray-600 mb-6">
          Connectez-vous pour postuler à cette offre
        </p>
        <button
          onClick={() => navigate('/login')}
          className={cn(
            "w-full inline-flex justify-center items-center",
            "px-6 py-3",
            "bg-teal-600 text-white",
            "hover:bg-teal-700",
            "rounded-xl",
            "text-sm font-medium",
            "transition-colors duration-200"
          )}
        >
          <Send className="h-5 w-5 mr-2" />
          Se connecter pour postuler
        </button>
      </div>
    );
  }

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <div className="text-center py-6">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-teal-100 mb-6">
            <CheckCircle2 className="h-8 w-8 text-teal-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Candidature envoyée !
          </h2>
          <p className="text-gray-600 mb-6">
            Votre candidature pour le poste de <span className="font-medium">{jobTitle}</span> chez <span className="font-medium">{companyName}</span> a bien été envoyée.
          </p>
          {/* <button
            onClick={() => navigate('/applications')}
            className={cn(
              "inline-flex justify-center items-center",
              "px-6 py-3",
              "bg-teal-600 text-white",
              "hover:bg-teal-700",
              "rounded-xl",
              "text-sm font-medium",
              "transition-colors duration-200"
            )}
          >
            <FileText className="h-4 w-4 mr-2" />
            Voir mes candidatures
          </button> */}
        </div>
      </motion.div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">
        Postuler à cette offre
      </h2>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6"
          >
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center gap-3 text-red-700">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm font-medium">{error}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <form onSubmit={handleApply} className="space-y-6">
  <div className="space-y-2">
    <label className="block text-sm font-medium text-gray-700">
      Lettre de motivation <span className="text-gray-400">(optionnelle)</span>
    </label>
    <textarea
      value={coverLetter}
      onChange={(e) => setCoverLetter(e.target.value)}
      rows={6}
      className={cn(
        "block w-full rounded-xl border transition-all duration-200",
        "bg-white/50 backdrop-blur-sm",
        "border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20",
        "placeholder-gray-400",
        "text-gray-900",
        "px-4 py-3"
      )}
      placeholder="Expliquez pourquoi vous êtes intéressé par ce poste..."
    />
  </div>

  <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 text-sm text-teal-800 flex items-start gap-3">
    <Info className="h-5 w-5 flex-shrink-0 text-teal-500 mt-0.5" />
    <div>
      <p>
        Votre <strong>CV sera automatiquement envoyé</strong> au recruteur avec votre lettre de motivation.
        Assurez-vous que vos informations sont à jour dans votre profil.
      </p>
    </div>
  </div>

  <button
    type="submit"
    disabled={loading}
    className={cn(
      "w-full inline-flex justify-center items-center",
      "px-6 py-3",
      "bg-teal-600 text-white",
      "hover:bg-teal-700",
      "rounded-xl",
      "text-sm font-medium",
      "shadow-lg shadow-teal-100",
      "transform transition-all duration-200",
      "hover:scale-105",
      "disabled:opacity-50 disabled:cursor-not-allowed",
    )}
  >
    {loading ? (
      <>
        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
        Envoi en cours...
      </>
    ) : (
      <>
        <Send className="h-4 w-4 mr-2" />
        Envoyer ma candidature
      </>
    )}
  </button>
</form>

    </div>
  );
}